import React, { useEffect } from 'react';

import SendIcon from '@mui/icons-material/Send';
import { styled } from '@mui/material/styles';
import {
    Button,
    Divider,
    FormControl,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { StyledH2 } from 'common/components';
import ReactHookFormInput from 'common/components/form/ReactHookFormInput';
import yupResolver from 'common/utils/yupResolver';

import * as actions from './actions';
import MessagesList from './MessagesList';
import * as selectors from './selectors';
import validate from './validate';

const Wrapper = styled('div')`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const Section = styled('div')`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-height: 0;
    padding: 0 15px;
`;

const CommentSubmit = styled('form')`
    padding-bottom: 30px;
    position: relative;

    .MuiTextField-root {
        border-radius: 20px;
    }

    button {
        height: 36px;
        position: absolute;
        right: 1px;
        top: 1px;
        width: 106px;
        z-index: 100;
    }
`;

const Container = ({
    callback,
    description,
    documentId,
    documentType,
    fetchMessages,
    handleOnClickSend,
    initialValues,
    isFetching,
    messages,
    title,
    user,
    userType,
}) => {
    const { t } = useTranslation();

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validate),
    });

    useEffect(() => reset(initialValues), [initialValues]);

    useEffect(() => {
        fetchMessages({ documentId, documentType, user, userType });
    }, [documentId, documentType, user, userType, fetchMessages]);

    const handleOnClickSubmit = (values) => {
        handleOnClickSend(values, callback);
        reset({ message: '' });
    };

    return (
        <Wrapper>
            <Section>
                <StyledH2>{title}</StyledH2>
                <Typography>{description}</Typography>
                <Divider />
                <MessagesList messages={messages} />
                <CommentSubmit onSubmit={handleSubmit(handleOnClickSubmit)}>
                    <FormControl fullWidth error={!!errors.message}>
                        <ReactHookFormInput
                            control={control}
                            hasCounter
                            maxLength={255} // Coincide con validate.jsx
                            name="message"
                            placeholder={t('Add comment')}
                        />
                    </FormControl>
                    <Button disabled={isFetching} type="submit" variant="text">
                        <SendIcon />
                    </Button>
                </CommentSubmit>
            </Section>
        </Wrapper>
    );
};

Container.propTypes = {
    callback: PropTypes.func,
    description: PropTypes.string,
    documentId: PropTypes.string,
    documentType: PropTypes.string,
    fetchMessages: PropTypes.func,
    handleOnClickSend: PropTypes.func,
    initialValues: PropTypes.object,
    isFetching: PropTypes.bool,
    messages: PropTypes.array,
    title: PropTypes.string,
    user: PropTypes.object,
    userType: PropTypes.number,
};

const mapStateToProps = createStructuredSelector({
    initialValues: selectors.getInitialValues,
    isFetching: selectors.getSendIsFetching,
    messages: selectors.getMessages,
});

const mapDispatchToProps = (dispatch) => ({
    fetchMessages: (values) => dispatch(actions.fetchMessages(values)),
    handleOnClickSend: (values, callback) =>
        dispatch(actions.sendMessage(values, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Container);
